import { useState } from 'react';
import {
  Card,
  Heading,
  Text,
  Stack,
  VStack,
  Flex,
  IconButton,
  Button,
  Icon,
  Spacer,
  Container,
  Avatar,
  Center,
  HStack,
  Badge,
  StackSeparator,
} from '@chakra-ui/react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { User } from '@/lib/types';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import QuickSearchForm from '@/components/ui/quick-search-form';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import InviteUserModal from './invite-user-modal';
import ImportUsersModal from './import-users-modal';
import ChangeRoleModal from './change-role-modal';
import useModalToggle, { useModalValueToggle } from '@/lib/hooks/use-modal-toggle';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import api from '@/utils/api';

interface ItemProps {
  user: User;
  onUserDeleted: (userId: string) => void;
}

const Item = ({ user, onUserDeleted }: ItemProps) => {
  const [state, setState] = useState({
    isLoading: false,
    showDeleteModal: false,
  });
  const roleModal = useModalValueToggle<User>();
  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));

      const promise = api.post(`users/deactivate/${user.id}`);

      toaster.promise(promise, {
        success: {
          title: 'User was removed successfully',
        },
        error: {
          title: 'Failed to remove user',
        },
        loading: { title: 'Removing...', description: 'Please wait' },
      });

      await promise;
      setState((prev) => ({ ...prev, showDeleteModal: false }));
      onUserDeleted(user.id.toString());
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.message || 'Failed to remove user',
      });
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  return (
    <Stack direction={'row'}>
      <Center>
        <Avatar.Root
          width={10}
          height={10}
          bg={user.is_active ? 'brand.50' : 'gray.100'}
          color={user.is_active ? 'brand.500' : 'gray'}
        >
          <Avatar.Fallback name={`${user.first_name} ${user.last_name}`} />
        </Avatar.Root>
      </Center>
      <VStack align={'start'} gap={0}>
        <Text fontWeight={800} color={user.is_active ? 'black' : 'gray'}>
          {user.first_name} {user.last_name}
          <Badge ml={2} variant='surface' size='xs'>
            {user.role_name}
          </Badge>
        </Text>
        <Text color={'gray.600'} fontSize={'sm'}>
          {user.email}
        </Text>
      </VStack>
      <Spacer />
      <Flex justifyContent='center' mt={4}>
        {user.is_active && (
          <Tooltip content={'Change Role'}>
            <IconButton
              variant={'ghost'}
              aria-label={'Change Role'}
              size={'sm'}
              onClick={() => {
                roleModal.open(user);
              }}
            >
              <Icon as={PencilSquareIcon} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip content={'Delete User'}>
          <IconButton
            variant={'ghost'}
            aria-label={'Delete User'}
            size={'sm'}
            onClick={() => setState((prev) => ({ ...prev, showDeleteModal: true }))}
          >
            <Icon as={TrashIcon} />
          </IconButton>
        </Tooltip>
      </Flex>
      {roleModal.state && (
        <ChangeRoleModal
          {...roleModal.props}
          user={roleModal.state}
          onSuccess={() => navigate(0)}
        />
      )}
      <DestructiveModal
        isOpen={state.showDeleteModal}
        onCancel={() => setState((prev) => ({ ...prev, showDeleteModal: false }))}
        onConfirm={handleDelete}
        title={'Delete user'}
        confirmText={'Delete'}
        isLoading={state.isLoading}
      >
        <Text
          dangerouslySetInnerHTML={{
            __html: `Remove <strong>${user.first_name} ${user.last_name} - ${user.email}</strong> from users?`,
          }}
        />
      </DestructiveModal>
    </Stack>
  );
};

const Users = () => {
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();
  const users = useLoaderData() as User[];
  const [searchResults, setSearchResults] = useState<User[]>(users);
  const [allUsers, setAllUsers] = useState<User[]>(users);
  const inviteModal = useModalToggle();
  const importModal = useModalToggle();
  const navigate = useNavigate();

  const handleSearch = (search: string) => {
    if (search.length === 0) return setSearchResults(allUsers);
    const searchResults = allUsers.filter((user) => {
      return (
        user.first_name.toLowerCase().includes(search.toLowerCase()) ||
        user.last_name.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase())
      );
    });
    setSearchResults(searchResults);
  };

  const handleUserDeletion = (userId: string) => {
    setAllUsers((prev) => prev.filter((user) => user.id.toString() !== userId));
    setSearchResults((prev) => prev.filter((user) => user.id.toString() !== userId));
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'4xl'}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'normal', md: 'center' }}
              mb={4}
            >
              <Heading size='3xl' mb={{ base: 4, md: 0 }}>
                Users
              </Heading>
              <Spacer />
              {isAdmin && !isCancelled && (
                <Stack gap={4} direction={{ base: 'column', md: 'row' }}>
                  <Button colorPalette='brand' onClick={inviteModal.open}>
                    Invite New User
                  </Button>
                  <Button colorPalette='brand' onClick={importModal.open}>
                    Import Users
                  </Button>
                </Stack>
              )}
            </Flex>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'4xl'}>
            <HStack mb={8}>
              <QuickSearchForm handleSearch={handleSearch} />
            </HStack>
            <Stack gap={4} separator={<StackSeparator />}>
              {searchResults.map((user, index) => (
                <Item key={`user-${index}`} user={user} onUserDeleted={handleUserDeletion} />
              ))}
            </Stack>
          </Container>

          <InviteUserModal {...inviteModal.props} onSuccess={() => navigate(0)} />
          <ImportUsersModal {...importModal.props} onSuccess={() => navigate(0)} />
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
};

export default Users;
